import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { ForgeAPI, InvoiceAPI, formatCurrency } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { useCompany } from "../context/CompanyContext";
import { toast } from "sonner";
import {
  UploadCloud, FileArchive, Sparkles, Hammer, RefreshCw,
  Plus, Trash2, ArrowRight, ChevronDown, Lock, Wand2,
  CheckCircle2, AlertCircle, Loader2, Cpu, FileText,
} from "lucide-react";

const CURRENCIES = ["CAD", "USD", "EUR", "GBP", "AUD"];
const POSTURES = [
  { key: "low",  label: "Lean",  hint: "Conservative range, faster close" },
  { key: "mid",  label: "Mid",   hint: "Industry midpoint (default)" },
  { key: "high", label: "Premium", hint: "Top-of-market for senior solo" },
];

const ForgeStudioPage = () => {
  const { unlocked } = useCenturion();
  const { company } = useCompany();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [report, setReport] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [scope, setScope] = useState("");
  const [scopeLoading, setScopeLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const [currency, setCurrency] = useState("CAD");
  const [posture, setPosture] = useState("mid");

  const [client, setClient] = useState({
    company: "",
    address: "",
    line2: "",
    country: "Canada",
    invoice_number: "",
    title: "",
    invoice_date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    due_date: "",
  });

  const [issuing, setIssuing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Auto-suggest invoice number
  useEffect(() => {
    if (!client.invoice_number) {
      const next = `${company.invoice_number_prefix}-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900) + 100)}`;
      setClient((c) => ({ ...c, invoice_number: next }));
    }
    // eslint-disable-next-line
  }, [company.invoice_number_prefix]);

  const onPickFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList);
    const totalBytes = arr.reduce((s, f) => s + f.size, 0);
    if (totalBytes > 75 * 1024 * 1024) {
      toast.error("Uploads exceed 75 MB combined cap");
      return;
    }
    setFiles(arr);
    runAnalyze(arr);
  };

  const runAnalyze = useCallback(async (fileArr) => {
    setAnalyzing(true);
    setReport(null);
    setScope("");
    setQuote(null);
    try {
      const r = await ForgeAPI.analyze(fileArr);
      setReport(r);
      setStep(2);
      toast.success(`Analyzed ${fileArr.length} file${fileArr.length !== 1 ? "s" : ""}`);

      setScopeLoading(true);
      ForgeAPI.scope(r)
        .then((res) => setScope(res.scope_brief))
        .catch(() => setScope(""))
        .finally(() => setScopeLoading(false));

      runQuote(r, currency, posture);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
    // eslint-disable-next-line
  }, [currency, posture]);

  const runQuote = async (r, cur, post, refresh = false) => {
    setQuoting(true);
    try {
      const q = await ForgeAPI.quote({ report: r, currency: cur, posture: post, refresh_rates: refresh });
      setQuote(q);
    } catch (e) {
      console.error(e);
      toast.error("Pricing failed");
    } finally {
      setQuoting(false);
    }
  };

  const updateLineItem = (idx, patch) => {
    setQuote((prev) => {
      if (!prev) return prev;
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], ...patch };
      const it = items[idx];
      if (!it.fixed && it.hours != null && it.rate != null) {
        items[idx].amount = Math.round(Number(it.hours) * Number(it.rate) * 100) / 100;
      }
      const sub = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      return { ...prev, line_items: items, subtotal: round2(sub), total: round2(sub) };
    });
  };

  const addLineItem = () => setQuote((p) => ({
    ...p,
    line_items: [...(p?.line_items || []), { description: "", detail: "", hours: 0, rate: 0, amount: 0, fixed: false }],
  }));
  const removeLineItem = (idx) =>
    setQuote((p) => {
      const items = p.line_items.filter((_, i) => i !== idx);
      const sub = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      return { ...p, line_items: items, subtotal: round2(sub), total: round2(sub) };
    });

  const handleIssue = async () => {
    if (!unlocked) { toast.error("Issuer mode locked. Type 'centurion' to unlock."); return; }
    if (!client.company.trim()) { toast.error("Bill-To company is required"); return; }
    if (!quote || quote.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    setIssuing(true);

    const payload = {
      invoice_number: client.invoice_number || `${company.invoice_number_prefix}-${Date.now()}`,
      title: client.title || (report ? `Forge analysis — ${report.filename}` : ""),
      project_name: client.title || (report?.filename || "Forge-quoted project"),
      invoice_date: client.invoice_date,
      due_date: client.due_date || "",
      to_party: { company: client.company, address: client.address, line2: client.line2, country: client.country },
      line_items: quote.line_items.map((i) => ({
        description: i.description,
        detail: i.detail || null,
        hours: i.fixed ? null : (i.hours == null ? null : Number(i.hours)),
        rate: i.fixed ? null : (i.rate == null ? null : Number(i.rate)),
        amount: Number(i.amount) || 0,
        fixed: !!i.fixed,
      })),
      adjustments: [],
      subtotal: quote.subtotal,
      total: quote.total,
      currency,
      status: "sent",
      project_summary: report ? buildSummary(report) : null,
      project_stats: report ? buildStats(report) : null,
      stack_report: report,
      scope_brief_text: scope,
      rate_snapshot_id: quote.rate_snapshot_id,
      rate_sources: quote.rate_sources,
      posture,
    };

    try {
      const created = await InvoiceAPI.create(payload);
      toast.success("Invoice issued!");
      navigate(`/invoices/${created.id}`);
    } catch (e) {
      console.error(e);
      toast.error("Issue failed");
    } finally {
      setIssuing(false);
    }
  };

  // ====== Drag-drop handlers ======
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0) onPickFiles(dropped);
  };

  const reset = () => {
    setFiles([]); setReport(null); setQuote(null); setScope(""); setStep(1);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Heading */}
        <div className="mb-8">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#d4a54b] mb-3">
            <Hammer size={14} /> WXZA Forge™ · Invoice Studio
          </div>
          <h1 className="font-display text-[42px] md:text-[58px] leading-[1.05] tracking-tight">
            Drop a stack. Extract the truth.<br />
            <span className="text-[#d4a54b] italic">Price the work.</span>
          </h1>
          <p className="text-white/55 mt-4 max-w-2xl text-[14px] leading-[1.7]">
            Upload a project zip. AYI_all dissects every dependency, route, and contract — then prices it
            against a live, citation-backed market-rate ledger. Every number is auditable.
          </p>
        </div>

        {/* Locked banner */}
        {!unlocked && (
          <div className="glass p-4 mb-6 flex items-center gap-4">
            <Lock size={18} className="text-[#d4a54b]" />
            <div className="flex-1 text-[13px]">
              <span className="text-white/80 font-medium">Issuer mode is locked.</span>{" "}
              <span className="text-white/55">Type the keyword to issue invoices. (Hint: Roman elite officer.)</span>
            </div>
          </div>
        )}

        {/* Step bar */}
        <Stepper step={step} hasFile={files.length > 0} hasReport={!!report} hasQuote={!!quote} />

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 mt-8">
          {/* LEFT — Drop / Forensics */}
          <section className="space-y-6">
            {!report ? (
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`dropzone ${dragOver ? "over" : ""} p-12 text-center cursor-pointer`}
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
              >
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                />
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#e0bc62] to-[#7a5a18] flex items-center justify-center text-[#1a1306]">
                    {analyzing ? <Loader2 size={28} className="animate-spin" /> : <UploadCloud size={28} />}
                  </div>
                  <div>
                    <h3 className="text-[20px] font-semibold">
                      {analyzing ? "AYI_all is reading your stack…" : "Drop any files"}
                    </h3>
                    <p className="text-white/55 text-[13px] mt-1.5">
                      {analyzing
                        ? "Extracting frameworks, materials, routes, contracts, docs, and assets."
                        : "Project zip · loose code files · PDFs · designs · images · or any combination · 75 MB cap"}
                    </p>
                  </div>
                  {!analyzing && (
                    <div className="text-[11px] text-white/40 font-mono uppercase tracking-[0.2em]">
                      .zip · .ts/.tsx/.py · .pdf · .docx · .fig · .png · *
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <StackReport report={report} onReset={reset} />
            )}

            {/* Scope brief */}
            {report && (
              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] flex items-center gap-2">
                    <Sparkles size={14} /> AYI_all · Absolute Zero Scope Brief
                  </h3>
                  {scopeLoading && <Loader2 size={14} className="animate-spin text-white/50" />}
                </div>
                {scope ? (
                  <p className="text-[14px] leading-[1.85] text-white/80 whitespace-pre-wrap">{scope}</p>
                ) : scopeLoading ? (
                  <p className="text-white/40 text-[13px] italic">Generating deterministic brief…</p>
                ) : (
                  <p className="text-white/40 text-[13px] italic">No brief yet.</p>
                )}
              </div>
            )}
          </section>

          {/* RIGHT — Quote builder */}
          <section className="space-y-6">
            <div className="glass p-6">
              <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-4">Pricing Posture</h3>

              <div className="grid grid-cols-3 gap-2">
                {POSTURES.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => { setPosture(p.key); if (report) runQuote(report, currency, p.key); }}
                    className={`text-left p-3 rounded-md border transition ${posture === p.key ? "border-[#d4a54b] bg-[rgba(212,165,75,0.08)]" : "border-white/10 hover:border-white/20"}`}
                  >
                    <div className="text-[12px] font-semibold">{p.label}</div>
                    <div className="text-[10px] text-white/45 leading-snug mt-1">{p.hint}</div>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <div>
                  <label className="field-label">Currency</label>
                  <Select
                    value={currency}
                    onChange={(v) => { setCurrency(v); if (report) runQuote(report, v, posture); }}
                    options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => report && runQuote(report, currency, posture, true)}
                    disabled={!report || quoting}
                    className="btn-ghost w-full justify-center"
                  >
                    <RefreshCw size={13} className={quoting ? "animate-spin" : ""} />
                    Refresh rates
                  </button>
                </div>
              </div>

              {quote?.rate_snapshot_id && (
                <div className="mt-4 text-[11px] text-white/40 font-mono">
                  ledger · <span className="text-[#d4a54b]/80">{quote.rate_snapshot_id}</span>
                </div>
              )}
            </div>

            {/* Bill to */}
            <div className="glass p-6">
              <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-4">Bill To</h3>
              <div className="space-y-3">
                <Field label="Invoice Number">
                  <input className="field" value={client.invoice_number} onChange={(e) => setClient({ ...client, invoice_number: e.target.value })} />
                </Field>
                <Field label="Project Title (optional)">
                  <input className="field" value={client.title} onChange={(e) => setClient({ ...client, title: e.target.value })} placeholder="What appears on dashboard" />
                </Field>
                <Field label="Client Company *">
                  <input className="field" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} placeholder="Acme Co." />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Address"><input className="field" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} /></Field>
                  <Field label="City / Postal"><input className="field" value={client.line2} onChange={(e) => setClient({ ...client, line2: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Invoice Date"><input className="field" value={client.invoice_date} onChange={(e) => setClient({ ...client, invoice_date: e.target.value })} /></Field>
                  <Field label="Due Date"><input className="field" value={client.due_date} onChange={(e) => setClient({ ...client, due_date: e.target.value })} placeholder="Net 30" /></Field>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Quote / line items */}
        {quote && (
          <section className="glass mt-8 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] flex items-center gap-2">
                <Cpu size={14} /> Forge-Generated Quote · Editable
              </h3>
              <button onClick={addLineItem} className="btn-ghost text-[12px] py-1.5 px-3">
                <Plus size={13} /> Add line
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-white/45 text-[10px] uppercase tracking-[0.14em] border-b border-[rgba(212,165,75,0.15)]">
                    <th className="py-2 font-medium">Service</th>
                    <th className="py-2 font-medium w-20 text-center">Hrs</th>
                    <th className="py-2 font-medium w-24 text-right">Rate</th>
                    <th className="py-2 font-medium w-28 text-right">Amount</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {quote.line_items.map((it, idx) => (
                    <tr key={idx} className="border-b border-white/5 align-top">
                      <td className="py-3 pr-3">
                        <input
                          className="field bg-transparent border-transparent hover:border-white/10 focus:border-[#d4a54b]/50 px-2 py-1 text-[13px]"
                          value={it.description}
                          onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                          placeholder="Description"
                        />
                        <input
                          className="field bg-transparent border-transparent hover:border-white/10 focus:border-[#d4a54b]/50 px-2 py-1 text-[11px] text-white/55 mt-1"
                          value={it.detail || ""}
                          onChange={(e) => updateLineItem(idx, { detail: e.target.value })}
                          placeholder="Basis / sub-text"
                        />
                      </td>
                      <td className="py-3 px-1">
                        <input
                          type="number" step="0.5"
                          className="field text-center w-20 px-1 py-1"
                          value={it.fixed ? "" : (it.hours ?? "")}
                          disabled={!!it.fixed}
                          onChange={(e) => updateLineItem(idx, { hours: e.target.value === "" ? null : Number(e.target.value) })}
                        />
                      </td>
                      <td className="py-3 px-1">
                        <input
                          type="number" step="1"
                          className="field text-right w-24 px-1 py-1"
                          value={it.fixed ? "" : (it.rate ?? "")}
                          disabled={!!it.fixed}
                          onChange={(e) => updateLineItem(idx, { rate: e.target.value === "" ? null : Number(e.target.value) })}
                        />
                      </td>
                      <td className="py-3 px-1 text-right">
                        <input
                          type="number" step="0.01"
                          className="field text-right w-28 px-1 py-1 font-mono text-[#e0bc62]"
                          value={it.amount}
                          onChange={(e) => updateLineItem(idx, { amount: Number(e.target.value) || 0, fixed: true })}
                        />
                        <label className="text-[10px] text-white/40 mt-1 inline-flex items-center gap-1">
                          <input type="checkbox" checked={!!it.fixed} onChange={(e) => updateLineItem(idx, { fixed: e.target.checked })} />
                          Fixed
                        </label>
                      </td>
                      <td className="py-3 px-1">
                        <button onClick={() => removeLineItem(idx)} className="text-white/40 hover:text-red-400 p-1.5 rounded">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="py-4 text-right text-[12px] text-white/55">Subtotal</td>
                    <td className="py-4 text-right font-mono text-[15px]">{formatCurrency(quote.subtotal, currency)}</td>
                    <td />
                  </tr>
                  <tr className="border-t border-[rgba(212,165,75,0.3)]">
                    <td colSpan={3} className="py-4 text-right text-[13px] uppercase tracking-[0.15em] text-[#d4a54b]">Total Due</td>
                    <td className="py-4 text-right font-mono text-[24px] text-[#e0bc62] font-bold">{formatCurrency(quote.total, currency)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            {quote.rate_sources?.length > 0 && (
              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2">Sources & Methodology</div>
                <ul className="space-y-1 text-[12px]">
                  {quote.rate_sources.slice(0, 5).map((s, i) => (
                    <li key={i} className="text-white/55">
                      <span className="text-[#d4a54b]/80">{s.publisher || "Source"}</span>
                      {s.url && <> · <a href={s.url} target="_blank" rel="noreferrer" className="underline hover:text-white">{s.url}</a></>}
                      {s.field && <span className="text-white/35"> · {s.field}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <div className="text-[12px] text-white/50">
                Pay rail: <span className="text-white/80">{company.payment_instructions}</span>
              </div>
              <button
                onClick={handleIssue}
                disabled={!unlocked || issuing}
                className="btn-primary disabled:opacity-50"
                title={!unlocked ? "Unlock issuer mode (type 'centurion')" : ""}
              >
                {!unlocked ? <Lock size={14} /> : <CheckCircle2 size={14} />}
                {issuing ? "Issuing…" : !unlocked ? "Locked" : "ISSUE INVOICE"} <ArrowRight size={14} />
              </button>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

const Stepper = ({ step, hasFile, hasReport, hasQuote }) => {
  const steps = [
    { label: "Drop", done: hasFile },
    { label: "Forensics", done: hasReport },
    { label: "Quote", done: hasQuote },
    { label: "Issue", done: false },
  ];
  return (
    <div className="glass p-3 px-5 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.16em]">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <span className={`flex items-center gap-1.5 ${s.done ? "text-[#e0bc62]" : "text-white/40"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.done ? "bg-[#e0bc62]" : "bg-white/20"}`} /> {s.label}
          </span>
          {i < steps.length - 1 && <span className="text-white/15">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

const StackReport = ({ report, onReset }) => {
  const flags = report.flags || {};
  return (
    <div className="glass p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] flex items-center gap-2">
          <FileArchive size={14} /> Stack Forensics ·{" "}
          <span className="text-white/80 normal-case tracking-normal font-normal">
            {report.filename}
          </span>
        </h3>
        <button onClick={onReset} className="text-[11px] text-white/50 hover:text-white">Reset</button>
      </div>

      {report._uploaded_filenames?.length > 0 && (
        <div className="mb-4 px-3 py-2 bg-white/[0.03] border border-white/8 rounded-md text-[11px] text-white/55 leading-[1.6]">
          <span className="text-white/40 uppercase tracking-[0.14em] mr-2">Uploaded:</span>
          {report._uploaded_filenames.slice(0, 8).join(", ")}
          {report._uploaded_filenames.length > 8 ? `, +${report._uploaded_filenames.length - 8} more` : ""}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
        <Stat label="Files" value={report.file_count} />
        <Stat label="LOC ~" value={report.approx_lines_of_code?.toLocaleString()} />
        <Stat label="Stack Class" value={report.stack_class?.toUpperCase()} accent />
        <Stat label="Routes" value={report.routes_count} />
        <Stat label="Components" value={report.components_count} />
        <Stat label="Contracts" value={(report.contracts || []).length} />
      </div>

      {(report.media && Object.values(report.media || {}).some((v) => v > 0)) && (
        <>
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-2">Media & Assets</div>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-5">
            {Object.entries(report.media)
              .filter(([, v]) => v > 0)
              .map(([k, v]) => (
                <div key={k} className="text-[11px] text-white/70">
                  <span className="font-mono text-white">{v}</span>{" "}
                  <span className="text-white/45 capitalize">{k}</span>
                </div>
              ))}
          </div>
        </>
      )}

      <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-2">Frameworks & Materials</div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {(report.frameworks || []).map((f) => (
          <span key={f} className="px-2.5 py-1 rounded text-[11px] bg-[rgba(212,165,75,0.1)] border border-[rgba(212,165,75,0.25)] text-[#e0bc62] font-mono">
            {f}
          </span>
        ))}
        {report.frameworks?.length === 0 && <span className="text-white/40 text-[12px]">No major frameworks detected.</span>}
      </div>

      <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-2">Languages</div>
      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(report.languages || {}).slice(0, 6).map(([lang, count]) => (
          <span key={lang} className="text-[11px] text-white/70">
            <span className="font-mono text-white">{count}</span> <span className="text-white/45">{lang}</span>
          </span>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Flag label="AI" on={flags.ai_integration} />
        <Flag label="Web3" on={flags.web3} />
        <Flag label="Auth" on={flags.auth} />
        <Flag label="Payments" on={flags.payments} />
        <Flag label="Tests" on={flags.has_tests} />
        <Flag label="Docs" on={flags.has_docs} />
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mb-0.5">{label}</div>
    <div className={`text-[18px] font-semibold tracking-tight font-mono ${accent ? "text-[#e0bc62]" : ""}`}>
      {value ?? "—"}
    </div>
  </div>
);

const Flag = ({ label, on }) => (
  <div className={`text-[11px] flex items-center gap-2 px-2.5 py-1.5 rounded border ${on ? "text-[#22c55e] border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.05)]" : "text-white/35 border-white/10"}`}>
    {on ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {label}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, options }) => (
  <div className="relative">
    <select className="field appearance-none pr-9 cursor-pointer" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o.value} value={o.value} className="bg-[#0e0c12]">{o.label}</option>)}
    </select>
    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
  </div>
);

const round2 = (n) => Math.round(Number(n) * 100) / 100;

const buildSummary = (report) => {
  const fr = (report.frameworks || []).slice(0, 6).join(", ");
  return `Forge-quoted project. Stack class: ${report.stack_class?.toUpperCase()}. Frameworks: ${fr}. ${report.file_count} files, ~${report.approx_lines_of_code} LOC, ${report.routes_count} routes, ${report.components_count} components.`;
};

const buildStats = (report) => {
  const langs = Object.entries(report.languages || {}).slice(0, 3).map(([k, v]) => `${v} ${k}`).join(" · ");
  return `${report.file_count} files | ${report.approx_lines_of_code?.toLocaleString()} LOC | ${langs} | ${(report.frameworks || []).slice(0, 4).join(", ")}`;
};

export default ForgeStudioPage;
